import * as THREE from 'three';
import { mergeGeometries } from 'three/addons/utils/BufferGeometryUtils.js';
import type { MapDefinition, Point } from '../../../shared/game/world-types.js';
import { PROP_RADIUS } from '../../../shared/game/prop-footprints.js';
import { samplePath } from '../../../shared/game/path-curve.js';
import { clearSegment, pointSegmentDistance } from '../../../shared/navigation/geometry.js';
import type { AnimatedProp } from './coast-props.js';
import { contactShadow, type SceneryBatches } from './scenery-batches.js';
import { createTreeWind, createWindMaterial, type TreeWind } from './wind-material.js';
import { weatheredMaterial } from './prop-detail.js';
import { naturalDetails, segmentGeometry } from './natural-details.js';
import { GrassField } from './grass-field.js';

type Biome = NonNullable<MapDefinition['scenery']>['districts'][number]['biome'];

export const palette = {
  grass: [0x5c7640, 0x6a7f44, 0x4e6838, 0x587a3c],
  meadowGrass: [0x58753c, 0x66803f, 0x4b6a35, 0x6f7c3e],
  dry: [0xb29b6b, 0xa88f63, 0xbfa874],
  heath: [0x8b776b, 0x9c817d, 0x79755c],
  heather: [0x77607a, 0x846a82, 0x6a566c, 0x8d7488],
  gorse: [0xa38a3c, 0xb09a4a],
  bush: [0x3d5a2d, 0x4d6838, 0x34502a, 0x566a34],
  pineBush: [0x34503f, 0x41604a, 0x2f4a41],
  wheat: [0xd0a948, 0xc79a3f, 0xdcb85c],
  reed: [0x8f8a4c, 0xa18d5a, 0x76803f],
  flower: [0xc47a90, 0xd8be62, 0xd9d5c6, 0x9382c0, 0xc4684c],
  pine: [0x4f604f, 0x68705b, 0x6e6a4e, 0x3f5a4d],
  stone: [0x848069, 0x727060, 0x8e8a74],
};

/** Largest scale of a scattered (walk-over) bush and stone; anything bigger is an authored obstacle. */
const SCATTER_BUSH_MAX = .5, SCATTER_STONE_MAX = .25;

/** A rooted, rounded thicket: uneven shoulders surround one full crown in all three dimensions. */
export function createBushGeometry() {
  const parts: THREE.BufferGeometry[] = [];
  const part = (source: THREE.BufferGeometry, color: number) => {
    const geometry = source.index ? source.toNonIndexed() : source;
    if (geometry !== source) source.dispose();
    const tint = new THREE.Color(color), colors: number[] = [];
    for (let i = 0; i < geometry.getAttribute('position').count; i++) colors.push(tint.r, tint.g, tint.b);
    geometry.setAttribute('color', new THREE.Float32BufferAttribute(colors, 3)); parts.push(geometry);
  };
  for (let i = 0; i < 3; i++) {
    const a = i * 2.1;
    part(segmentGeometry([0, 0, 0], [Math.cos(a) * .25, .64, Math.sin(a) * .25], .035, .012), 0x897e62);
  }
  // Offset around the root, rather than arranging flattened discs in a row or a vertical tier.
  const lobes = [
    [.02, .78, -.08, .58, 1.05, 0xf0f3e4],
    [-.36, .52, .22, .46, 1.08, 0xdde7cf],
    [.36, .62, .28, .43, 1.12, 0xe7edda],
    [.29, .50, -.42, .42, 1.12, 0xd4e0c4],
    [-.35, .68, -.32, .40, 1.10, 0xe3ebd5],
  ];
  for (const [i, [x, y, z, r, height, tint]] of lobes.entries()) {
    const canopy = new THREE.IcosahedronGeometry(1, i === 0 ? 2 : 1).rotateY(i * 1.37).rotateZ(i * .19);
    const positions = canopy.getAttribute('position');
    for (let v = 0; v < positions.count; v++) {
      const px = positions.getX(v), py = positions.getY(v), pz = positions.getZ(v);
      const swell = 1 + .045 * Math.sin(px * 5 + pz * 3 + i) + .03 * Math.sin(py * 6 - pz * 4);
      positions.setXYZ(v, px * r * swell + x, py * r * height * swell + y, pz * r * swell + z);
    }
    canopy.computeVertexNormals();
    part(canopy, tint);
  }
  // Small leaf pairs nest into the outer shoulders, breaking the surface without fragmenting the clump.
  for (const [i, lobe] of lobes.slice(1).entries()) {
    const [x, y, z, r, height] = lobe, angle = Math.atan2(z, x);
    const normal = new THREE.Vector3(Math.cos(angle) * .8, .6, Math.sin(angle) * .8);
    const anchor = new THREE.Vector3(x + normal.x * r * .98, y + normal.y * r * height * .98, z + normal.z * r * .98);
    for (const side of [-1, 1]) {
      const leaf = new THREE.IcosahedronGeometry(1, 0).scale(.095, .035, .18).rotateY(side * .55)
        .applyQuaternion(new THREE.Quaternion().setFromUnitVectors(new THREE.Vector3(0, 1, 0), normal))
        .rotateY(i * .12).translate(anchor.x, anchor.y, anchor.z);
      part(leaf, side > 0 ? 0xe3edcf : 0xc7d7b4);
    }
  }
  const geometry = mergeGeometries(parts)!;
  parts.forEach(part => part.dispose());
  return geometry;
}
/** A tuft is five slender blades fanned out from one root, merged into one instance so fields read as grass. */
export function createGrassClump() {
  return mergeGeometries([0, 1, 2, 3, 4].map(k => {
    const height = k % 2 ? .78 : 1, blade = new THREE.ConeGeometry(.13, height, 3); blade.translate(0, height / 2, 0); // root at the origin, so it leans from the ground
    blade.rotateZ(.32 + (k % 3) * .08); blade.rotateY(k / 5 * Math.PI * 2 + .4); blade.translate(Math.cos(k * 1.3) * .08, -.5, Math.sin(k * 1.3) * .08);
    return blade;
  }))!;
}

export interface FloraContext {
  map: MapDefinition; random: () => number; batches: SceneryBatches;
  coastEdge(z: number): number; shared: THREE.Material;
  groundColor?: (x: number, z: number, target: THREE.Color) => THREE.Color;
  geometries: { rock: THREE.BufferGeometry; trunk: THREE.BufferGeometry; crown: THREE.BufferGeometry; tuft: THREE.BufferGeometry; box: THREE.BufferGeometry };
}

/** A woodland pine: a trunk and two stacked crown tiers, every part bending from the foot. Each tier batches apart, as each has its own foot. */
export function woodlandPine(batches: SceneryBatches, geometries: { trunk: THREE.BufferGeometry; crown: THREE.BufferGeometry }, timber: THREE.Material, trees: TreeWind,
  x: number, z: number, h: number, s: number, turn: number, lower: number, upper: number) {
  batches.add('woodland-trunk', geometries.trunk, trees.sway(timber, -.5), x, h * .32, z, .15, h * .64, .15, 0, 0x665445);
  batches.add('woodland-pine-lower', geometries.crown, trees.material(-.52 / .55), x, h * .52, z, s, h * .55, s, turn, lower);
  batches.add('woodland-pine-upper', geometries.crown, trees.material(-.73 / .5), x + s * .06, h * .73, z, s * .65, h * .5, s * .65, turn + .6, upper);
}

/** Densely dress the ground. Everything here is cosmetic: authored obstacles own all collision. */
export function scatterFlora({ map, random, batches, coastEdge, shared, geometries, groundColor }: FloraContext): { wind: AnimatedProp; pine: THREE.Material; trees: TreeWind; grass: THREE.Material; field?: GrassField } {
  const scenery = map.scenery!;
  // Sway is in world metres at the tip: grass whips, bushes nod, trees barely lean. `pineWind` is kept for thicket obstacles.
  // Walkers part grass around their feet, bow flowers, reeds and ferns over from the root and lean the small scattered bushes aside.
  const grassWind = createWindMaterial(.3, .5, 1, .62, 1.22, { lean: 1.35 }), pineWind = createWindMaterial(.18, .5, 1, .9, 1.05);
  const bushWind = createWindMaterial(.14, 0, 1, .85, 1.08, { lean: 1, bow: .45, whole: true });
  const canopyWind = createWindMaterial(.12, 0, 1.4, .85, 1.08, { lean: .45, whole: true }), trees = createTreeWind(.18);
  pineWind.material.vertexColors = true; bushWind.material.vertexColors = true; canopyWind.material.vertexColors = true;
  const bushGeometry = createBushGeometry();
  const timber = weatheredMaterial(0xffffff, 'wood', { flatShading: true });
  const puddle = new THREE.CylinderGeometry(1, 1, .02, 10);
  const details = naturalDetails(batches, timber, bushWind.material, trees, random);
  const pick = (list: number[]) => list[Math.floor(random() * list.length)];
  // The district whose centre is proportionally closest wins, so small places (marsh, village) are not swallowed by big woods.
  const biomeAt = (x: number, z: number): Biome | undefined => scenery.districts.filter(d => Math.hypot(x - d.x, z - d.z) < d.radius)
    .sort((a, b) => Math.hypot(x - a.x, z - a.z) / a.radius - Math.hypot(x - b.x, z - b.z) / b.radius)[0]?.biome;
  // Plants keep off the road surface as it is actually drawn: the same centripetal curve as the renderer, bucketed
  // into a 16 m grid. The keep-out reaches the nominal edge only, so grass grows into the ragged verge as on a real track.
  const CELL = 16, roadCells = new Map<string, { a: Point; b: Point; half: number }[]>();
  for (const path of scenery.paths) {
    const curve = samplePath(path.points, Math.max(16, Math.ceil(path.points.slice(1).reduce((sum, b, i) => sum + Math.hypot(b.x - path.points[i].x, b.z - path.points[i].z), 0) / 2)));
    for (let i = 1; i < curve.length; i++) {
      const a = curve[i - 1], b = curve[i], reach = path.width / 2 + 3;
      for (let cx = Math.floor((Math.min(a.x, b.x) - reach) / CELL); cx <= Math.floor((Math.max(a.x, b.x) + reach) / CELL); cx++)
        for (let cz = Math.floor((Math.min(a.z, b.z) - reach) / CELL); cz <= Math.floor((Math.max(a.z, b.z) + reach) / CELL); cz++) {
          const key = cx + ',' + cz; let list = roadCells.get(key); if (!list) roadCells.set(key, list = []);
          list.push({ a, b, half: path.width / 2 });
        }
    }
  }
  const near = (p: Point, padding: number) => (roadCells.get(Math.floor(p.x / CELL) + ',' + Math.floor(p.z / CELL)) ?? [])
    .some(segment => pointSegmentDistance(p, segment.a, segment.b) < segment.half + Math.max(0, padding - 1));

  // Grass tone drifts across the land in broad patches (lusher hollows, sun-bleached rises) with per-tuft jitter on top.
  const tone = new THREE.Color();
  const vary = (color: number, x: number, z: number) => {
    const drift = Math.sin(x * .043 + Math.cos(z * .031) * 2.1) + Math.sin(z * .057 - x * .021) * .8;
    tone.setHex(color).offsetHSL(drift * .022 + (random() - .5) * .035, (random() - .5) * .12, drift * .035 + (random() - .5) * .07);
    return tone.getHex();
  };
  const clump = createGrassClump();
  // Meadow, heath, pine and crop tufts give way to the continuous field (grass-field.ts); shore, sand, scree, cobble and marsh keep theirs.
  // A replaced tuft still draws its random numbers, so the rest of the layout is the same with or without the field.
  const cover = scenery.grassField ?? 'ribbons', fieldGrows = cover !== 'tufts';
  const blade = (x: number, z: number, h: number, s: number, color: number, replaced = false) => {
    const turn = random() * 6, tint = vary(color, x, z);
    if (!fieldGrows || !replaced) batches.add('grass', clump, grassWind.material, x, h / 2, z, s * 1.5, h, s * 1.5, turn, tint, false);
  };
  // Medium and large bushes and boulders are authored obstacles (see growThickets); scattered ones stay small enough to walk through.
  const bush = (x: number, z: number, size: number, color: number) => { const s = Math.min(size, SCATTER_BUSH_MAX); batches.add('bush', bushGeometry, canopyWind.material, x, 0, z, s, s, s, random() * 6, color, true); };
  // Stones big enough to read cast a real shadow and darken the grass at their foot; the smallest are left as flecks.
  const pebble = (x: number, z: number, size: number) => {
    const s = Math.min(size, SCATTER_STONE_MAX), big = s > .28;
    batches.add(big ? 'stone' : 'pebble', geometries.rock, shared, x, s * .22, z, s, s * .62, s * .8, random() * 6, pick(palette.stone), big);
    if (s > .2) contactShadow(batches, x, z, s * 1.45, x * 13 + z * 7);
  };
  const flower = (x: number, z: number) => details.flower(x, z, pick(palette.flower));

  // Yards stay clear: no plants grow inside a prop's footprint (plus a margin around buildings).
  const footprints = scenery.props.filter(prop => PROP_RADIUS[prop.kind] > 0).map(prop => ({ x: prop.x, z: prop.z, r: PROP_RADIUS[prop.kind] * (prop.scale ?? 1) * (prop.kind === 'house' || prop.kind === 'chapel' ? 1.3 : 1) }));
  const inProp = (p: Point) => footprints.some(f => Math.abs(p.x - f.x) < f.r && Math.abs(p.z - f.z) < f.r && Math.hypot(p.x - f.x, p.z - f.z) < f.r);
  // The continuous field asks about every candidate root while the game runs, so it asks indexed versions of the same tests: footprints
  // bucketed like the roads, and the biome of each metre square, worked out once.
  const propCells = new Map<string, typeof footprints>();
  for (const f of footprints) for (let cx = Math.floor((f.x - f.r) / CELL); cx <= Math.floor((f.x + f.r) / CELL); cx++)
    for (let cz = Math.floor((f.z - f.r) / CELL); cz <= Math.floor((f.z + f.r) / CELL); cz++) {
      const key = cx + ',' + cz; let list = propCells.get(key); if (!list) propCells.set(key, list = []); list.push(f);
    }
  const inPropFast = (p: Point) => (propCells.get(Math.floor(p.x / CELL) + ',' + Math.floor(p.z / CELL)) ?? []).some(f => Math.hypot(p.x - f.x, p.z - f.z) < f.r);
  const BIOMES: (Biome | undefined)[] = [undefined, 'meadow', 'pine', 'heath', 'sand', 'cobble', 'farmland', 'marsh', 'scree'];
  const metres = Math.ceil(map.width) + 1, biomeCodes = new Uint8Array(metres * (Math.ceil(map.depth) + 1));
  const biomeNear = (x: number, z: number) => {
    const i = Math.floor(z + map.depth / 2) * metres + Math.floor(x + map.width / 2);
    if (!biomeCodes[i]) biomeCodes[i] = 1 + BIOMES.indexOf(biomeAt(Math.floor(x) + .5, Math.floor(z) + .5));
    return BIOMES[biomeCodes[i] - 1];
  };
  // Counts are tuned for the Ember Coast (1200 × 880 m); smaller maps (scenarios) get the same density, not the same number.
  const areaScale = map.width * map.depth / (1200 * 880), scaled = (count: number) => Math.round(count * areaScale);
  const total = scaled(190000);
  for (let i = 0; i < total; i++) {
    let x = (random() - .5) * (map.width - 6), z = (random() - .5) * (map.depth - 6);
    const biome = biomeAt(x, z);
    if (biome === 'farmland') x = Math.floor(x / 2.4) * 2.4 + 1.2 + (random() - .5) * .35;
    const p = { x, z }, r0 = random();
    if (!clearSegment(map, p, p, 1) || near(p, r0 < .5 ? 1.1 : 2) || inProp(p)) continue;
    const shore = x - coastEdge(z), r = random();
    if (shore < 22) {
      if (r < .45) blade(x, z, .25 + random() * .5, .3 + random() * .35, pick(palette.dry));
      else if (r < .62 && shore > 6) bush(x, z, .4 + random() * .5, 0x8a8a52);
      else pebble(x, z, .15 + random() * .5);
      continue;
    }
    switch (biome) {
      case 'pine':
        if (r < .03 && shore > 35) {
          const h = 10 + random() * 8, s = h * (.16 + random() * .12);
          details.roots(x, z, .9);
          woodlandPine(batches, geometries, timber, trees, x, z, h, s, x * .1, pick(palette.pine), pick(palette.pine));
        } else if (r < .3) bush(x, z, .5 + random() * .8, pick(palette.pineBush));
        else if (r < .85) blade(x, z, .2 + random() * .45, .25 + random() * .4, pick(palette.grass), true);
        else pebble(x, z, .15 + random() * .35);
        break;
      case 'heath':
        if (r < .1) bush(x, z, .35 + random() * .55, pick(palette.heather));
        else if (r < .125) bush(x, z, .4 + random() * .4, pick(palette.gorse));
        else if (r < .8) blade(x, z, .2 + random() * .4, .25 + random() * .4, pick(palette.heath), true);
        else pebble(x, z, .2 + random() * .6);
        break;
      case 'farmland':
        if (Math.floor((x - 1.2) / 2.4) % 5 === 0) { if (r < .1) pebble(x, z, .2 + random() * .3); break; }
        blade(x, z, .65 + random() * .3, .22 + random() * .14, pick(palette.wheat), true);
        break;
      case 'marsh':
        if (r < .045) batches.add('puddle', puddle, shared, x, .045, z, 1 + random() * 3, 1, 1 + random() * 2.4, random() * 6, 0x35575c, false);
        else if (r < .38) blade(x, z, .8 + random() * .9, .22 + random() * .3, pick(palette.reed));
        else if (r < .5) details.reed(x, z, .7 + random() * .5);
        else if (r < .6) bush(x, z, .5 + random() * .5, pick(palette.bush));
        else blade(x, z, .18 + random() * .3, .3 + random() * .3, pick(palette.grass));
        break;
      case 'scree':
        if (r < .55) pebble(x, z, .2 + random() * .9);
        else if (r < .66) { contactShadow(batches, x, z, .45); batches.add('boulder', geometries.rock, shared, x, .12, z, .22 + random() * .18, .16 + random() * .18, .2 + random() * .16, random() * 6, pick(palette.stone), true); } // small enough to walk over: anything bigger needs an authored rock obstacle
        else if (r < .8) blade(x, z, .15 + random() * .3, .25, pick(palette.dry));
        break;
      case 'cobble':
        if (r < .5) pebble(x, z, .12 + random() * .35);
        else if (r < .7) blade(x, z, .12 + random() * .3, .2, pick(palette.dry));
        else if (r < .74) bush(x, z, .3 + random() * .4, 0x6a6a42);
        break;
      case 'sand':
        if (r < .3) blade(x, z, .22 + random() * .35, .3, pick(palette.dry));
        else if (r < .55) pebble(x, z, .12 + random() * .4);
        else if (r < .58) batches.add('shell', geometries.rock, shared, x, .05, z, .22, .08, .16, random() * 6, 0xe6d9c4, false);
        break;
      default: // meadow and open ground
        if (r < .08) bush(x, z, .45 + random() * .8, pick(palette.bush));
        else if (r < .21) { blade(x, z, .3 + random() * .4, .3 + random() * .3, pick(palette.meadowGrass), true); flower(x + .25, z); if (random() < .4) flower(x - .2, z + .2); }
        else if (r < .8) blade(x, z, .18 + random() * .6, .25 + random() * .45, pick(palette.meadowGrass), true);
        else pebble(x, z, .15 + random() * .65);
    }
  }
  // Landscape features: clustered set-pieces that give each biome its own character at a glance.
  const ok = (x: number, z: number, padding = .8) => { const q = { x, z }; return clearSegment(map, q, q, padding) && !near(q, 1.6 + padding) && !inProp(q); }; // set-pieces keep their whole footprint off the road
  const features = { logs: 0, stumps: 0, mushrooms: 0, ferns: 0, outcrops: 0, drifts: 0, trees: 0 };
  for (let i = 0, count = scaled(5200); i < count; i++) {
    const cx = (random() - .5) * (map.width - 20), cz = (random() - .5) * (map.depth - 20), biome = biomeAt(cx, cz), r = random();
    if (cx - coastEdge(cz) < 30 || !ok(cx, cz, 2)) continue;
    if (biome === 'pine' || biome === 'marsh') {
      if (r < .22) { // a fallen, weathered log with moss along its back and a broken branch stub
        const length = 3 + random() * 5, angle = random() * Math.PI, radius = .28 + random() * .2;
        details.log(cx, cz, length, radius, angle);
        features.logs++;
      } else if (r < .4) { // stumps with a ring of shoots
        for (let s = 0; s < 1 + Math.floor(random() * 3); s++) {
          const x = cx + (random() - .5) * 5, z = cz + (random() - .5) * 5, w = .35 + random() * .25;
          if (!ok(x, z, .5)) continue;
          details.stump(x, z, w);
          features.stumps++;
        }
      } else if (r < .62) { // a fairy ring or clump of mushrooms
        const red = random() < .4, count = 4 + Math.floor(random() * 8), ring = random() < .3;
        for (let m = 0; m < count; m++) {
          const a = m / count * Math.PI * 2 + random() * .3, d = ring ? 1.2 + random() * .6 : random() * 1.2, x = cx + Math.cos(a) * d, z = cz + Math.sin(a) * d, s = .08 + random() * .1;
          details.mushroom(x, z, s * 1.5, red && m % 3 === 0 ? 0 : 1 + m % 2);
        }
        features.mushrooms++;
      } else { // a bed of ferns
        for (let f = 0; f < 3 + Math.floor(random() * 5); f++) {
          const x = cx + (random() - .5) * 4, z = cz + (random() - .5) * 4, s = .7 + random() * .6, tone = pick([0x4f6b3c, 0x5d7a43, 0x46613a]);
          if (!ok(x, z, .5)) continue;
          details.fern(x, z, s, tone);
          features.ferns++;
        }
      }
    } else if (biome === 'heath' || biome === 'scree' || biome === undefined || biome === 'meadow') {
      if (r < .3) { // a lichen-spotted outcrop: a few big rocks bedded in grass
        for (let k = 0; k < 3 + Math.floor(random() * 4); k++) {
          const x = cx + (random() - .5) * 6, z = cz + (random() - .5) * 6, s = .2 + random() * .18; // cosmetic only, so kept small enough to walk over
          if (!ok(x, z, s * .8)) continue;
          contactShadow(batches, x, z, s * 1.4);
          batches.add('outcrop', geometries.rock, shared, x, s * .1, z, s * (1 + random() * .5), s * (.45 + random() * .35), s,random() * 6, pick([0x7e7a67, 0x6f6c5c, 0x898470]), true);
          batches.add('lichen', geometries.rock, shared, x, s * .42, z, s * .55, s * .14, s * .5, random() * 6, pick([0x7c7d44, 0x6c7e44, 0x8e8a5c]), false);
          for (let g = 0; g < 5; g++) blade(x + (random() - .5) * s * 2.4, z + (random() - .5) * s * 2.4, .3 + random() * .4, .3, pick(palette.meadowGrass), biome !== 'scree');
        }
        features.outcrops++;
      } else if (r < .62 && biome !== 'scree') { // a drift of one kind of wildflower
        const tone = pick(palette.flower);
        for (let k = 0; k < 18 + Math.floor(random() * 22); k++) {
          const x = cx + (random() - .5) * 7, z = cz + (random() - .5) * 5;
          if (ok(x, z, .3)) details.flower(x, z, tone);
        }
        features.drifts++;
      } else if (r < .7 && biome !== 'scree') { // a lone wind-bent tree, crown leaning away from the sea
        const h = 7 + random() * 4;
        if (!ok(cx, cz, 2)) continue;
        const lean = -.2 + random() * .4;
        if (scenery.trees === false) continue;
        details.tree(cx, cz, h, lean);
        features.trees++;
      }
    }
  }
  // Sward: a dense second pass of grass on open, grassy ground, thickest in hollows (the same drift that tints it).
  for (let i = 0, count = scaled(140000); i < count; i++) {
    const x = (random() - .5) * (map.width - 6), z = (random() - .5) * (map.depth - 6), biome = biomeAt(x, z);
    if (biome && biome !== 'meadow' && biome !== 'heath' && biome !== 'pine') continue;
    const lush = Math.sin(x * .043 + Math.cos(z * .031) * 2.1) + Math.sin(z * .057 - x * .021) * .8;
    if (random() > .55 + lush * .22 || x - coastEdge(z) < 24) continue;
    const p = { x, z };
    if (!clearSegment(map, p, p, .6) || near(p, .9) || inProp(p)) continue;
    const colors = biome === 'heath' ? palette.heath : biome === 'pine' ? palette.grass : palette.meadowGrass;
    blade(x, z, .28 + random() * .5 + Math.max(0, lush) * .15, .35 + random() * .35, pick(colors), true);
  }
  // Crop rows are planted, not scattered: a tuft every metre along each furrow.
  for (const field of scenery.districts.filter(d => d.biome === 'farmland')) {
    for (let row = -Math.floor(field.radius / 2.4); row <= field.radius / 2.4; row++) {
      if (row % 5 === 0) continue;
      const x = Math.round((field.x + row * 2.4 - 1.2) / 2.4) * 2.4 + 1.2;
      for (let z = field.z - field.radius; z < field.z + field.radius; z += 1) {
        if (Math.hypot(x - field.x, z - field.z) > field.radius * .93) continue;
        const p = { x, z: z + random() * .6 };
        if (clearSegment(map, p, p, .8) && !near(p, 1) && !inProp(p)) blade(p.x, p.z, .7 + random() * .35, .2 + random() * .1, pick(palette.wheat), true);
      }
    }
  }
  // Reeds, driftwood and tide wrack along the entire tidal edge.
  for (let i = 0, count = Math.round(700 * map.depth / 880); i < count; i++) {
    const z = -map.depth / 2 + 5 + random() * (map.depth - 10), x = coastEdge(z) + 2 + random() * 15, p = { x, z };
    if (!clearSegment(map, p, p, .6)) continue;
    const r = random();
    if (r < .16) details.driftwood(x, z, 2 + random() * 3.5, random() * 6);
    else if (r < .26) details.kelp(x - random() * 3, z, .7 + random());
    else blade(x, z, .45 + random() * .6, .5, pick(palette.reed));
  }
  details.disposeUnused();
  // Crops follow the painted fields (coast-visual.ts), which every farm district paints whichever district is nearest.
  const farms = scenery.districts.filter(d => d.biome === 'farmland');
  const onFarm = (p: Point) => farms.some(d => Math.hypot(p.x - d.x, p.z - d.z) < d.radius * .85);
  const field = cover === 'ribbons' || cover === 'cards' ? new GrassField(map, cover, {
    meadow: p => {
      if (p.x - coastEdge(p.z) <= 16 || onFarm(p)) return 0;
      const biome = biomeNear(p.x, p.z);
      if (biome && biome !== 'meadow' && biome !== 'heath' && biome !== 'pine') return 0;
      if (near(p, .9) || inPropFast(p) || !clearSegment(map, p, p, .25)) return 0;
      return THREE.MathUtils.smoothstep(p.x - coastEdge(p.z), 16, 30);
    },
    // Stalks stand on the ground's painted crop bands (coast-visual.ts: sin(x · 2.62) > .1), every fifth band left as a track.
    crop: p => {
      if (!farms.length || Math.sin(p.x * 2.62) < .3 || !onFarm(p)) return false;
      const band = Math.floor((p.x * 2.62 - .1) / (Math.PI * 2));
      if (((band % 5) + 5) % 5 === 0) return false;
      return clearSegment(map, p, p, .25) && !near(p, 1) && !inPropFast(p);
    },
    height: (x, z) => batches.height(x, z), color: groundColor,
  }) : undefined;
  return { wind: { update: time => { field?.update(time); grassWind.update(time); pineWind.update(time); bushWind.update(time); canopyWind.update(time); trees.update(time); } }, pine: pineWind.material, trees, grass: grassWind.material, field };
}
